import { useEffect, useState } from "react";
import api from "../../services/api";
import { Search, Trash2, Video, Calendar, Link as LinkIcon, Edit2, X } from "lucide-react";
import toast from "react-hot-toast";

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      {[1, 2, 3, 4, 5].map(i => (
        <td key={i} className="px-6 py-4">
          <div className="h-4 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
        </td>
      ))}
    </tr>
  );
}

export default function InstituteLiveClasses() {
  const [lives, setLives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editModal, setEditModal] = useState({ open: false, live: null });
  const [page, setPage] = useState(1);
  const PER_PAGE = 10;

  const load = () => {
    setLoading(true);
    api.get("/institute/live-classes")
      .then(res => setLives(res.data))
      .catch(() => toast.error("Failed to load live classes"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleDelete = () => {
    if (!confirmDelete) return;
    api.delete(`/institute/live-classes/${confirmDelete.id}`)
      .then(() => {
        toast.success("Live class deleted");
        setConfirmDelete(null);
        load();
      })
      .catch((err) => toast.error(err.response?.data?.detail || "Failed to delete live class"));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const { live } = editModal;
    try {
      await api.put(`/live/${live.id}`, {
        scheduled_at: live.scheduled_at,
        recording_url: live.recording_url
      });
      toast.success("Live class updated successfully!");
      setEditModal({ open: false, live: null });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  

  const filtered = lives.filter(l =>
    l.course_title?.toLowerCase().includes(search.toLowerCase()) ||
    l.meeting_id?.toLowerCase().includes(search.toLowerCase())
  );
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-900 dark:text-white">Live Classes</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">{lives.length} active or scheduled sessions</p>
        </div>
      </div>

      <div className="relative">
        <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by course or meeting ID..."
          className="w-full pl-11 pr-4 py-2.5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-sm outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
        />
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400 text-xs uppercase tracking-wider">
                <th className="px-6 py-3.5 text-left font-semibold">Course</th>
                <th className="px-6 py-3.5 text-left font-semibold">Meeting ID</th>
                <th className="px-6 py-3.5 text-left font-semibold">Scheduled At</th>
                <th className="px-6 py-3.5 text-left font-semibold">Recording</th>
                <th className="px-6 py-3.5 text-left font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {loading
                ? [1, 2, 3, 4, 5].map(i => <SkeletonRow key={i} />)
                : paginated.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-16 text-center">
                      <Video size={40} className="mx-auto text-slate-300 dark:text-slate-700 mb-3" />
                      <p className="text-slate-500 font-medium">No sessions found</p>
                    </td>
                  </tr>
                ) : paginated.map(live => (
                  <tr key={live.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                    <td className="px-6 py-4">
                      <span className="font-semibold text-slate-800 dark:text-white">{live.course_title}</span>
                    </td>
                    <td className="px-6 py-4 text-slate-500 dark:text-slate-400 font-mono text-xs">
                      {live.meeting_id}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 text-xs">
                        <Calendar size={14} />
                        <span>{live.scheduled_at ? new Date(live.scheduled_at).toLocaleString() : "Not set"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {live.recording_url ? (
                        <a href={live.recording_url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-blue-600 hover:underline font-medium text-xs">
                          <LinkIcon size={12} /> View Recording
                        </a>
                      ) : (
                        <span className="text-slate-400 text-xs italic">No recording</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditModal({ open: true, live: { ...live } })}
                          className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Edit session"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setConfirmDelete(live)}
                          className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete session"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t dark:border-slate-800 flex items-center justify-between">
            <p className="text-sm text-slate-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Prev</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {editModal.open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold dark:text-white">Edit Live Session</h3>
              <button onClick={() => setEditModal({ open: false, live: null })} className="text-slate-400 hover:text-slate-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Scheduled At</label>
                <input
                  type="datetime-local"
                  value={editModal.live.scheduled_at ? new Date(editModal.live.scheduled_at).toISOString().slice(0, 16) : ""}
                  onChange={e => setEditModal(prev => ({ ...prev, live: { ...prev.live, scheduled_at: e.target.value } }))}
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Recording URL</label>
                <input
                  type="text"
                  value={editModal.live.recording_url || ""}
                  onChange={e => setEditModal(prev => ({ ...prev, live: { ...prev.live, recording_url: e.target.value } }))}
                  placeholder="https://..."
                  className="w-full px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button type="button" onClick={() => setEditModal({ open: false, live: null })} className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors dark:text-white">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 transition-colors">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-sm p-6 shadow-2xl">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">Delete Live Class?</h3>
            <p className="text-slate-500 dark:text-slate-400 text-sm mb-6">
              Are you sure you want to delete this live class session? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 py-2.5 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
